import { ProjectsController } from 'controllers/Projects/ProjectsController'
import { useController } from 'customHooks/useController'
import { SignalRService } from 'features/SignalR/service/SignalRService'
import { throttle } from 'lodash'
import { useCallback, useState } from 'react'
import { ProjectSummaryDto } from 'services/APIs/InternalAPI/internal-api.contracts'
import { HttpGetMetaData } from 'services/APIs/InternalAPI/InternalAPI'
import { ShowException } from 'store/Application/appActions'
import { useOrganizationContext } from 'utils/useOrganizationContext'
import { useLocalStorage } from '../../customHooks/useLocalStorage'

const getGroupName = (partyId: string) => `${partyId}_project_list`

export const useProjectList = () => {
  const [onlyMyProjects, setOnlyMyProjects] = useLocalStorage<boolean>(
    'filterMyProjects',
    false
  )
  const [pageSize, setPageSize] = useLocalStorage<number>(
    'projectListPageSize',
    100
  )

  const [projectList, setProjectList] = useState<ProjectSummaryDto[]>(undefined)
  const [projectStates, setProjectStates] = useState<string[]>(undefined)

  const { controller, loading } = useController(() => new ProjectsController())

  const fetchProjectList = useCallback(async () => {
    try {
      const projectList = await controller.GetProjectsList()
      return projectList
    } catch (err) {
      ShowException('project list', err)
      return undefined
    }
  }, [controller])

  const fetchProjectListPaged = useCallback(
    async (meta?: HttpGetMetaData<ProjectSummaryDto>) => {
      try {
        const projectList = await controller.GetProjectsListPaged(
          onlyMyProjects,
          meta
        )
        return projectList
      } catch (err) {
        ShowException('project list', err)
        return undefined
      }
    },
    [controller, onlyMyProjects]
  )

  const updateProjectList = useCallback(async () => {
    const newProjectList = await fetchProjectList()

    setProjectList(newProjectList.projectList)
    setProjectStates(newProjectList.projectStates)
  }, [fetchProjectList])

  const updateProjectListPaged = useCallback(
    async (meta?: HttpGetMetaData<ProjectSummaryDto>) => {
      const pagedResponse = await fetchProjectListPaged(meta)

      setProjectList(pagedResponse?.details)
      setProjectStates(pagedResponse?.projectStates)
    },
    [fetchProjectListPaged]
  )

  const updateProjectStatus = async (
    initialStatus: string,
    targetStatus: string,
    position: number,
    projectSummary: ProjectSummaryDto
  ) => {
    setProjectList((current) => {
      return current
        .map((x) =>
          x.id === projectSummary.id
            ? {
                ...x,
                status: targetStatus,
                lastOperation: new Date(),
              }
            : x
        )
        .sort(
          (a, b) =>
            new Date(b.lastOperation).getTime() -
            new Date(a.lastOperation).getTime()
        )
    })

    await controller.UpdateProjectStatus(
      initialStatus,
      targetStatus,
      position,
      projectSummary
    )
  }

  const handleSetPriority = useCallback(
    async (projectId: string, priority: number) => {
      setProjectList((current) => {
        return current
          .map((x) =>
            x.id === projectId
              ? {
                  ...x,
                  priority: priority,
                  lastOperation: new Date(),
                }
              : x
          )
          .sort(
            (a, b) =>
              new Date(b.lastOperation).getTime() -
              new Date(a.lastOperation).getTime()
          )
      })

      await controller.UpdateProjectPriority(projectId, priority)
    },
    [controller]
  )

  const { organizationId: partyId } = useOrganizationContext()
  const [isConnected, setIsConnected] = useState(false)

  const connectToUpdates = useCallback(
    (callback: () => void) => {
      const debouncedCallback = throttle(
        () => {
          callback()
        },
        1000 * 30,
        { leading: false, trailing: true }
      )

      const onServerUpdate = () => debouncedCallback()

      const connectToSignalR = async () => {
        const hub = await SignalRService.GetHub()
        hub.registerHandler('onProjectsListUpdates', onServerUpdate)
        hub.JoinGroup(getGroupName(partyId), () => {
          console.info('receving project updates')
          setIsConnected(true)
        })
      }

      connectToSignalR()
    },
    [partyId]
  )

  const disconnectFromUpdates = useCallback(() => {
    SignalRService.GetHub().then((hub) => {
      hub.unregister('onProjectsListUpdates')
      hub.LeaveGroup(getGroupName(partyId), () =>
        console.info('will not receive project list updates')
      )
    })
    setIsConnected(false)
  }, [partyId])

  return {
    onlyMyProjects,
    setOnlyMyProjects,
    projectList,
    projectStates,
    updateProjectListPaged,
    updateProjectList,
    fetchProjectListPaged,
    pageSize,
    setPageSize,
    loading,
    updateProjectStatus,
    connectToUpdates,
    disconnectFromUpdates,
    isConnected,
    groupName: getGroupName(partyId),
    handleSetPriority,
  }
}
